import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DialpadIcon from '@mui/icons-material/Dialpad';

const VoiceWave = ({ data }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !containerRef.current) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let increment = 0;

    const resizeCanvas = () => {
      canvas.width = containerRef.current.offsetWidth;
      canvas.height = containerRef.current.offsetHeight;
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // Wave Configuration: Calm, Intelligent Blue/Purple
    const waves = [
      { wavelength: 200, amplitude: 50, speed: 0.01, color: 'rgba(0, 229, 255, 0.4)' }, // Cyan
      { wavelength: 100, amplitude: 30, speed: 0.02, color: 'rgba(213, 0, 249, 0.3)' }, // Purple
      { wavelength: 150, amplitude: 40, speed: 0.015, color: 'rgba(100, 255, 218, 0.2)' }, // Teal
    ];

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      increment += 0.03; // Slower speed for a "smart" feel

      waves.forEach((wave, i) => {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);

        for (let x = 0; x < canvas.width; x++) {
          // Complex sine wave math for organic "voice" look
          const y = canvas.height / 2 + 
            Math.sin(x / wave.wavelength + increment * (i + 1)) * wave.amplitude * Math.sin(x / canvas.width * Math.PI); // Taper ends to 0
          
          ctx.lineTo(x, y);
        }

        ctx.strokeStyle = wave.color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <Box 
      ref={containerRef}
      sx={{
        position: 'relative',
        minHeight: '80vh', // Takes up significant space
        background: 'radial-gradient(circle at 50% 50%, #1a1a2e 0%, #0a0a1a 100%)', // Deep Void Blue
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        py: 8
      }}
    >
      {/* The Wave Canvas in the background */}
      <canvas 
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          opacity: 0.8
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: '800px', textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
            {/* IVR accent: icon + glowing line + icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3 }}>
                <PhoneInTalkIcon
                    sx={{
                        color: '#00e5ff',
                        fontSize: 28,
                        filter: 'drop-shadow(0 0 6px #00e5ff)',
                    }}
                />
                <Box
                    sx={{
                        width: 60,
                        height: 4,
                        bgcolor: '#00e5ff',
                        borderRadius: 2,
                        boxShadow: '0 0 10px #00e5ff',
                    }}
                />
                <DialpadIcon
                    sx={{
                        color: '#00e5ff',
                        fontSize: 28,
                        filter: 'drop-shadow(0 0 6px #00e5ff)',
                    }}
                />
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: '#fff' }}>
                {data.title}
            </Typography>

            <Typography variant="h6" sx={{ color: '#b0bec5', mb: 4, lineHeight: 1.6, maxWidth: '90%' }}>
                {data.description}
            </Typography>

            {/* Feature List */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4, justifyContent: 'center' }}>
                {Object.values(data.features).map((feature, idx) => (
                  <Chip 
                    key={idx}
                    label={feature}
                    sx={{ 
                      color: '#fff', 
                      borderColor: 'rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(4px)'
                    }} 
                    variant="outlined" 
                  />
                ))}
              </Box>

              {/* Tech Stack - Smaller/Technical */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#546e7a', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                  Built with
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {(Array.isArray(data.techStack) ? data.techStack : Object.values(data.techStack || {})).map((tech, i) => (
                     <Typography key={i} variant="body2" sx={{ color: '#00e5ff', fontFamily: 'monospace' }}>
                       {tech}
                     </Typography>
                  ))}
                </Box>
              </Box>
            </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default VoiceWave;
